import { useRef, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Check, Truck, Lock, ShieldCheck, MoveHorizontal } from "lucide-react";
import {
  PRODUCT, VARIANTS,
  heroBenefits, heroBadges, trustStrip, whyChoose, keyIngredients, resultsData,
} from "../lib/data";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { Logo } from "../components/Logo";
import { Reviews } from "../components/Reviews";
import { StockBadge } from "../components/StockBadge";
import { MobileStickyBar } from "../components/MobileStickyBar";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import { useImages, useVideos } from "../context/ContentContext";
import { toast } from "sonner";
import { api } from "../lib/api";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const nav = useNavigate();
  const { universe } = useTheme();
  const { t, lang } = useLang();
  const IMG = useImages();
  const VIDEOS = useVideos();
  const heroImg = universe === "femme" ? IMG.femmeBottle : IMG.heroBottle;
  const heroVideo = VIDEOS.heroHome || "/assets/luxe-oryvea-hero.mp4";
  const badges = heroBadges(lang);
  const benefits = heroBenefits(lang);
  return (
    <section ref={ref} className="relative overflow-hidden grain pt-28 pb-16" data-testid="hero">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 62% 34%, var(--lo-glow), transparent 50%)" }} />
        <div className="absolute inset-0" style={{ background: "var(--lo-hero-grad)" }} />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_0.9fr_auto] gap-8 lg:gap-6 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="mb-4"><Logo size="lg" /></div>
            <p className="font-display text-3xl md:text-4xl tracking-[0.25em] mt-2" style={{ color: "var(--lo-text)", fontWeight: 300 }}>SÉRUM CAPILLAIRE</p>
            <p className="overline mt-3 mb-6" style={{ letterSpacing: "0.25em" }}>{t("hero.tagline")}</p>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }} className="text-sm leading-relaxed max-w-md mb-8" style={{ color: "var(--lo-muted)" }}>
            {t("hero.description")}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.8 }} className="flex flex-wrap gap-4 mb-10">
            <button className="btn-primary h-14 px-9" onClick={() => nav(universe === "femme" ? "/serum-femme" : "/serum-homme")} data-testid="hero-cta-primary">{t("common.discover_serum")}</button>
            <Link to="/marque" className="btn-ghost inline-flex items-center h-14 px-9" data-testid="hero-cta-secondary">{t("common.learn_more")}</Link>
          </motion.div>
          <div className="flex flex-wrap gap-x-8 gap-y-5 mb-6">
            {badges.map((b, i) => (
              <motion.div key={b.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.06 }} className="flex flex-col items-center text-center w-20">
                <div className="w-11 h-11 rounded-full border flex items-center justify-center mb-2" style={{ borderColor: "var(--lo-border-rose)" }}>
                  <Icon name={b.icon} size={18} stroke="var(--lo-gold)" strokeWidth={1.2} />
                </div>
                <span className="overline leading-tight" style={{ fontSize: "0.5rem", color: "var(--lo-muted)" }}>{b.label}</span>
              </motion.div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={15} fill="var(--lo-gold)" stroke="var(--lo-gold)" />)}</div>
            <span className="overline" style={{ fontSize: "0.62rem" }}>{t("hero.clients")}</span>
          </div>
        </div>

        <motion.div style={{ y }} className="relative flex justify-center">
          <div className="absolute -inset-6 rounded-[2rem] blur-3xl" style={{ background: "radial-gradient(circle,var(--lo-glow),transparent 70%)" }} />
          <video
            key={heroVideo}
            src={heroVideo.startsWith("http") ? heroVideo : heroVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="relative w-full max-w-[42rem] aspect-video object-cover rounded-[1.5rem] shadow-2xl border"
            style={{ borderColor: "var(--lo-border-rose)" }}
            data-testid="hero-video"
            aria-label="LUXE ORYVEA Premium — présentation pour elle et pour lui"
          />
        </motion.div>

        <div className="hidden lg:flex flex-col gap-8 pl-2">
          {benefits.map((b) => (
            <div key={b.title} className="flex flex-col items-center text-center w-28">
              <Icon name={b.icon} size={22} stroke="var(--lo-gold)" strokeWidth={1.1} className="mb-2" />
              <span className="overline leading-tight" style={{ fontSize: "0.52rem", color: "var(--lo-muted)" }}>{b.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const { lang } = useLang();
  const items = trustStrip(lang);
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-2 mb-4">
      <div className="card-lux p-8 grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="trust-strip">
        {items.map((t) => (
          <div key={t.title} className="flex items-start gap-3">
            <Icon name={t.icon} size={26} stroke="var(--lo-gold)" strokeWidth={1} className="shrink-0 mt-0.5" />
            <div>
              <p className="overline mb-1" style={{ fontSize: "0.58rem" }}>{t.title}</p>
              <p className="text-xs leading-snug" style={{ color: "var(--lo-muted)" }}>{t.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhyChoose() {
  const { universe } = useTheme();
  const { t, lang } = useLang();
  const IMG = useImages();
  const img = universe === "femme" ? IMG.femmeDropper : IMG.dropper;
  const whyList = whyChoose(lang);
  const keyIng = keyIngredients(lang);
  return (
    <section className="py-20 md:py-28" id="ingredients" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-3 gap-10 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-4 rounded-full blur-3xl" style={{ background: "radial-gradient(circle,var(--lo-glow),transparent 70%)" }} />
            <img src={img} alt="Sérum LUXE ORYVEA" loading="lazy" decoding="async" className="relative w-full h-[460px] object-contain" data-testid="why-img" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="overline mb-3">{t("why.overline")}</p>
          <h2 className="font-display text-4xl sm:text-5xl mb-8" style={{ fontWeight: 300 }}>LUXE ORYVEA <span className="rose-gold-text italic">?</span></h2>
          <ul className="space-y-4 mb-8">
            {whyList.map((w) => (
              <li key={w} className="flex items-center gap-3 text-sm" style={{ color: "var(--lo-text)" }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ border: "1px solid var(--lo-gold)" }}><Check size={12} stroke="var(--lo-gold)" /></span>
                {w}
              </li>
            ))}
          </ul>
          <Link to="/formule" className="btn-primary inline-flex items-center h-14 px-8" data-testid="why-cta">{t("why.discover_formula")}</Link>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="card-lux p-8" data-testid="key-ingredients-card">
            <p className="overline text-center mb-6">{t("why.key_ingredients")}</p>
            <div className="space-y-5">
              {keyIng.map((ing) => (
                <div key={ing.name} className="flex gap-4 items-start">
                  <div className="w-11 h-11 rounded-full border flex items-center justify-center shrink-0 overflow-hidden" style={{ borderColor: "var(--lo-border-rose)" }}>
                    <img src={IMG[ing.image]} alt={ing.name} loading="lazy" decoding="async" className="w-full h-full object-cover rounded-full" />
                  </div>
                  <div>
                    <p className="font-display text-lg" style={{ fontWeight: 400, color: "var(--lo-gold)" }}>{ing.name}</p>
                    <p className="text-xs leading-snug" style={{ color: "var(--lo-muted)" }}>{ing.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="gold-line my-6" />
            <p className="text-center overline" style={{ fontSize: "0.58rem" }}>{t("why.free_claims")}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Results() {
  const { t, lang } = useLang();
  const { universe } = useTheme();
  const IMG = useImages();
  const results = resultsData(lang);
  const resultPhotos = universe === "femme"
    ? [IMG.femmeResult1, IMG.femmeResult2]
    : [IMG.hommeResult1, IMG.hommeResult2, IMG.hommeResult3];
  return (
    <section className="py-16 md:py-24" id="resultats" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal><h2 className="font-display text-4xl sm:text-5xl mb-10 tracking-wide" style={{ fontWeight: 300 }}>{t("results.title")}</h2></Reveal>
          <div className="grid grid-cols-2 gap-8">
            {results.map((r, i) => (
              <Reveal key={r.label} delay={i * 0.08}>
                <div>
                  <p className="font-display text-5xl mb-1 rose-gold-text" data-testid={`result-${i}`}>{r.value}</p>
                  <p className="text-xs" style={{ color: "var(--lo-muted)" }}>{r.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-[11px] mt-8" style={{ color: "var(--lo-muted)" }}>{t("results.note")}</p>
        </div>
        <Reveal delay={0.15}>
          <div className="flex flex-col gap-3">
            {resultPhotos.map((src, i) => (
              <div key={i} className="relative overflow-hidden card-lux p-0">
                <img src={src} alt="Résultats LUXE ORYVEA" loading="lazy" decoding="async" className="w-full object-cover" data-testid={`result-photo-${i}`} />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-center mt-3" style={{ color: "var(--lo-muted)" }}>{t("results.illustration")}</p>
        </Reveal>
      </div>
    </section>
  );
}

function OfferBand() {
  const { add } = useCart();
  const nav = useNavigate();
  const { universe } = useTheme();
  const { t } = useLang();
  const IMG = useImages();
  const [catalog, setCatalog] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const offerImg = universe === "femme" ? IMG.femmeOffer : IMG.offer;
  const serumKey = universe === "femme" ? "serum_femme" : "serum_homme";
  const serumProduct = catalog.find((p) => p.lookup_key === serumKey);
  const serumStock = Number(serumProduct?.stock ?? 0);
  // Un stock absent signifie « pas encore chargé », pas « épuisé ».
  const stockKnown = catalogLoaded && !!serumProduct;
  const out = stockKnown && serumStock <= 0;
  useEffect(() => {
    api.get("/products").then((r) => { setCatalog(r.data || []); setCatalogLoaded(true); }).catch(() => setCatalogLoaded(true));
  }, []);
  const addToCart = () => {
    if (!stockKnown) { toast.error("Vérification de la disponibilité…"); return; }
    if (out) { toast.error("Produit épuisé"); return; }
    const v = VARIANTS.single;
    const offerLabel = universe === "femme" ? "Sérum Femme" : "Sérum Homme";
    // Utilise l'image d'offre actuellement définie dans le CMS admin.
    // Le libellé et la clé doivent suivre l'univers affiché sur l'accueil.
    const added = add({ key: serumKey, label: `${offerLabel} — ${v.sub}`, unit: v.unit, image: offerImg, universe }, 1);
    if (!added) {
      toast.error(`Votre panier contient déjà une autre gamme.`);
      return;
    }
    toast.success(t("offer.added"));
    nav("/panier");
  };
  return (
    <section className="py-12 md:py-20" data-testid="offer-band">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="card-lux p-8 md:p-12 grid lg:grid-cols-[0.8fr_1fr_1fr] gap-10 items-center relative overflow-hidden">
          <div className="absolute -left-10 -bottom-10 w-56 h-56 rounded-full blur-3xl" style={{ background: "radial-gradient(circle,rgba(231,201,121,0.18),transparent 70%)" }} />
          <div className="relative"><img src={offerImg} alt="Coffret Sérum Capillaire" loading="lazy" decoding="async" className="w-full h-64 object-contain" data-testid="offer-img" /></div>
          <div className="relative">
            <p className="overline mb-2">{t("offer.overline")}</p>
            <h2 className="font-display text-3xl md:text-4xl mb-3" style={{ fontWeight: 300 }}>Sérum Capillaire</h2>
            <p className="text-sm mb-5" style={{ color: "var(--lo-muted)" }}>{t("offer.desc")}</p>
            <div className="flex items-end gap-3 mb-6">
              <span className="font-display text-4xl rose-gold-text">{PRODUCT.price.toFixed(2).replace(".", ",")} €</span>
              <span className="text-lg line-through mb-1" style={{ color: "var(--lo-muted)" }}>39,90 €</span>
            </div>
            <button className="btn-primary h-14 px-10 disabled:opacity-40 disabled:cursor-not-allowed" disabled={!stockKnown || out} onClick={addToCart} data-testid="offer-add-btn">{out ? "Épuisé" : !stockKnown ? "Vérification…" : t("common.add_to_cart")}</button>
          </div>
          <div className="relative grid grid-cols-3 gap-4 border-l pl-8" style={{ borderColor: "var(--lo-border)" }}>
            {[[Truck, t("offer.free_ship_title"), t("offer.free_ship_sub")], [Lock, t("offer.secure_title"), t("offer.secure_sub")], [ShieldCheck, t("offer.guarantee_title"), t("offer.guarantee_sub")]].map(([I, tt, s], i) => (
              <div key={i} className="text-center">
                <I size={24} stroke="var(--lo-gold)" strokeWidth={1} className="mx-auto mb-3" />
                <p className="overline mb-1" style={{ fontSize: "0.52rem" }}>{tt}</p>
                <p className="text-[10px]" style={{ color: "var(--lo-muted)" }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);
  return (
    <>
      <Hero />
      <TrustStrip />
      <WhyChoose />
      <Results />
      <OfferBand />
      <div id="avis"><Reviews /></div>
      <MobileStickyBar />
      <div className="h-16 md:hidden" />
    </>
  );
}
